import React from 'react';

import {
  BrowserNavigationService,
  ExternalNavigationContextProvider,
  ExternalNavigationService,
  ScreenWithOptionalParams,
  StackNavigationContextProvider,
} from 'core/navigation';
import { StyleUtil } from 'ui/util';
import { ConfigContextProvider } from './config';
import { DeveloperContextProvider, useDeveloperContext } from './developer';
import { ROOT_ROUTE } from './navigation';
import { RootNavigator } from './navigation/Root.navigator';
import { KalturaPlayerInitParams, KalturaPlayerVideoParams } from './KalturaPlayer';
import { useAppInitEffect } from './AppInit.effect';
import classNames from './EABD2App.module.scss';

const CONTACT_US_URL = 'https://www.ericsson.com/en/contact/sales-inquiry';
const KALTURA_PLAYER_PARAMS: KalturaPlayerInitParams = { partnerId: '1614541', subPartnerId: '161454100', playerId: '43311361' };
const KALTURA_PLAYER_CASE1_VIDEO_PARAMS: KalturaPlayerVideoParams = {
  targetId: 'kaltura_player_1600945515',
  cacheId: 1600945515,
  videoId: '1_1sl8g44j',
};
const KALTURA_PLAYER_CASE2_VIDEO_PARAMS: KalturaPlayerVideoParams = {
  targetId: 'kaltura_player_1600945442',
  cacheId: 1600945442,
  videoId: '1_b47wb1tp',
};
const KALTURA_PLAYER_CASE3_VIDEO_PARAMS: KalturaPlayerVideoParams = {
  targetId: 'kaltura_player_1600945269',
  cacheId: 1600945269,
  videoId: '1_8vhhsbtm',
};
const EXTERNAL_NAVIGATION_SERVICE: ExternalNavigationService = new BrowserNavigationService();
const INITIAL_SCREEN: ScreenWithOptionalParams = { screen: ROOT_ROUTE.HOME };
const SHOULD_EMULATE_ERICSSON_FRAME = process.env.NODE_ENV === 'development';

export const App = React.memo(() => {
  const { shouldEmulateEricssonFrame } = useDeveloperContext();

  useAppInitEffect();

  const RootClassNames = React.useMemo<string>(() => {
    const rootClassNames = StyleUtil.createInitialClassNameArray(classNames.root);

    if (shouldEmulateEricssonFrame()) {
      rootClassNames.push(classNames.embeddedRoot);
    }

    return StyleUtil.mergeClassNames(rootClassNames);
  }, [shouldEmulateEricssonFrame]);

  return (
    <div className={RootClassNames}>
      <ExternalNavigationContextProvider service={EXTERNAL_NAVIGATION_SERVICE}>
        <StackNavigationContextProvider initialScreen={INITIAL_SCREEN}>
          <RootNavigator />
        </StackNavigationContextProvider>
      </ExternalNavigationContextProvider>
    </div>
  );
});

export const AppWithContext = React.memo(() => {
  return (
    <DeveloperContextProvider shouldEmulateEricssonFrame={SHOULD_EMULATE_ERICSSON_FRAME}>
      <ConfigContextProvider
        contactUsUrl={CONTACT_US_URL}
        kalturaPlayerInitParams={KALTURA_PLAYER_PARAMS}
        kalturaPlayerCase1VideoParams={KALTURA_PLAYER_CASE1_VIDEO_PARAMS}
        kalturaPlayerCase2VideoParams={KALTURA_PLAYER_CASE2_VIDEO_PARAMS}
        kalturaPlayerCase3VideoParams={KALTURA_PLAYER_CASE3_VIDEO_PARAMS}>
        <App />
      </ConfigContextProvider>
    </DeveloperContextProvider>
  );
});
